import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { Link } from 'react-router';

import { replaceTemplateVariable } from 'helpers/LandingPageUtils';
import { Loader } from 'components/Loader';
import { formatTimeToUserTimezone, getDefaultTimezone } from 'helpers/meetSanta';
import { clearSantaError, fetchSantaAvailableTimeslots, onSantaPageLoaded, reserveSantaTimeslot, setTimeslotTimezone } from 'store/ducks/santa/actions';

import css from 'styles/components/landing/santasHelpers.scss';

const CAL_OFFSET = 2;
const MONTH_END_DAY = 31;

export const Calendar = ({ onDateSelect, selectedDate, timeSlotsByDay, eligibleDays, slotDetails: { day = {} } }) => {
  const generateDateSlots = () => {
    const breakDays = new Set([4, 11, 18, 25]);
    const calendarArr = [];
    let daysRowArr = [];

    for (let day = CAL_OFFSET * -1; day <= (MONTH_END_DAY + CAL_OFFSET); day++) {
      const isSelected = day === selectedDate; // selected date from local state
      const isEligible = timeSlotsByDay[day]; // check if day exists in timeSlotsByDay
      const isBooked = isEligible && !eligibleDays[day]; // check for eligible day, but has no `isAvailable` slots
      const isUninteractive = !isEligible || isBooked; // isnt eligible or is booked

      const cellDay = day < 1 || day > MONTH_END_DAY
        ? <td key={day} />
        : (
          <td
            key={day}
            className={cn({
              [css.eligible]: isEligible,
              [css.selected]: isSelected,
              [css.booked]: isBooked
            })}>

            <input
              type="radio"
              id={`radio-${day}`}
              aria-label={`December ${day}, 2021`}
              name="date"
              value={day}
              disabled={isUninteractive}
              onChange={onDateSelect.bind(this, day)}
            />
            <label htmlFor={`radio-${day}`}>{day}</label>
          </td>
        );

      daysRowArr.push(cellDay);

      // if the day is a break day or last day of the month, push row cells into main arary and clear it
      if (breakDays.has(day) || day === MONTH_END_DAY) {
        calendarArr.push(<tr>{daysRowArr}</tr>);
        daysRowArr = [];
      }
    }

    return calendarArr;
  };

  return (
    <fieldset>
      <legend className={css.legend}><h2>{day.heading}</h2></legend>

      {day.copy && <p className={css.subHeading}>{day.copy}</p>}
      <table className={css.calendarTable}>
        <thead>
          <tr>
            <th>Sun</th>
            <th>Mon</th>
            <th>Tue</th>
            <th>Wed</th>
            <th>Thu</th>
            <th>Fri</th>
            <th>Sat</th>
          </tr>
        </thead>
        <tbody>
          {generateDateSlots()}
        </tbody>
      </table>
    </fieldset>
  );
};

export const TimeSlots = ({ supportedTimeZones, timezone, isDateSelected = false, timeSelected, onTimeSelect, onChangeTimezone, selectedTimeSlots, slotDetails: { time = {} } }) => {
  const generatedTimeSlots = selectedTimeSlots.map(timeSlot => {
    const { isAvailable, value, label } = timeSlot;
    const { value: selectedValue, label: selectedLabel } = selectedTimeSlots?.find(selectedSlot => value === selectedSlot.value) || {};
    const isSelected = timeSelected === value;
    const isUninteractive = !isAvailable;

    return (
      <div
        key={value}
        className={cn(css.eligible, css.timeOption, {
          [css.selected]: isSelected,
          [css.booked]: isUninteractive
        })}>

        <input
          type="radio"
          id={`radio-time-${value}`}
          aria-label={label}
          name="time"
          value={value}
          disabled={isUninteractive}
          onClick={onTimeSelect.bind(this, selectedValue, selectedLabel)}
        />
        <label htmlFor={`radio-time-${value}`}>{label || value}</label>
      </div>
    );
  });

  return (
    <fieldset disabled={!isDateSelected}>
      <legend className={css.legend}><h2>{time.heading}</h2></legend>
      <label className={css.subHeading} htmlFor="timezone">Time Zone</label>
      <select id="timezone" onChange={onChangeTimezone} value={timezone}>
        {
          supportedTimeZones.map(({ value, label }) => <option key={value} value={value}>{label}</option>)
        }
      </select>

      <div className={css.cellContainer}>
        {generatedTimeSlots}
      </div>
    </fieldset>
  );
};

export const Messaging = ({ isBooked, errorCallingApi, showTimeDoesntWorkForCustomerManual, showTimeDoesntWorkForCustomer, slotDetails = {}, dayBooked, timeBooked = {}, timezone }) => {
  const { busy, confirmed, error, secondary, alternative, elfStartTime, elfEndTime } = slotDetails;
  let content;

  if (isBooked) {
    content = confirmed;
  } else if (errorCallingApi) {
    content = error;
  } else if (showTimeDoesntWorkForCustomer) {
    content = busy;
  } else if (showTimeDoesntWorkForCustomerManual) {
    content = alternative;
  }

  if (!content) {
    return null;
  }

  return (
    <div className={css.messaging}>
      <h2>{content.heading}</h2>
      {isBooked && <p className={css.bookedMessage}>{`December ${dayBooked} at ${timeBooked.label} (${timezone})`}</p>}
      <p>{replaceTemplateVariable(content.copy, [formatTimeToUserTimezone(elfStartTime), formatTimeToUserTimezone(elfEndTime), getDefaultTimezone()])}</p>
      <Link className={css.shoppingButton} to={secondary.link}>{secondary.cta}</Link>
    </div>
  );
};

export const SantasHelpers = props => {
  const {
    clearSantaError,
    fetchSantaAvailableTimeslots,
    meetSanta,
    slotDetails,
    setTimeslotTimezone,
    reserveSantaTimeslot,
    onSantaPageLoaded
  } = props;

  const [selectedDate, setDate] = useState(null);
  const [selectedTime, setTime] = useState(null);
  const [selectedTimeLabel, setTimeLabel] = useState(null);
  const [showTimeDoesntWorkForCustomerManual, setShowTimeDoesntWorkForCustomerManual] = useState(false);

  const {
    greeting = {},
    alternative = {},
    primary,
    fetchTimeSlotsEndpoint
  } = slotDetails || {};

  useEffect(() => {
    // make API call to get time slots
    fetchSantaAvailableTimeslots(fetchTimeSlotsEndpoint);
  }, [fetchSantaAvailableTimeslots, fetchTimeSlotsEndpoint]);

  const {
    defaultTimeSlots,
    isEligible,
    isBooked,
    errorCallingApi,
    isLoaded,
    isLoading,
    eligibleDays,
    timeSlotsByDay,
    dayBooked,
    timeBooked,
    timezone,
    showTimeDoesntWorkForCustomer,
    supportedTimeZones
  } = meetSanta || {};

  useEffect(() => {
    if (isLoaded) {
      onSantaPageLoaded();
    }
  }, [isLoaded, onSantaPageLoaded]);

  useEffect(() => {
    if (showTimeDoesntWorkForCustomerManual || showTimeDoesntWorkForCustomer || errorCallingApi) {
      window?.scrollTo(0, 0);
    }
  }, [showTimeDoesntWorkForCustomerManual, showTimeDoesntWorkForCustomer, errorCallingApi]);

  const clearTimeState = () => {
    setTime(null);
    setTimeLabel(null);
  };

  const handleDateSelect = day => {
    setDate(day);
    clearTimeState();
    clearSantaError();
    setShowTimeDoesntWorkForCustomerManual(false);
  };

  const handleTimeSelection = (value, label) => {
    setTime(value);
    setTimeLabel(label);
    clearSantaError();
    setShowTimeDoesntWorkForCustomerManual(false);
  };

  const handleChangeTimezone = ({ currentTarget }) => {
    clearTimeState();
    setTimeslotTimezone(currentTarget.value);
  };

  const handleSubmit = e => {
    e.preventDefault();
    reserveSantaTimeslot(selectedDate, selectedTime, selectedTimeLabel);
  };

  const handleTimeDoesntWorkForCustomerClick = () => {
    setShowTimeDoesntWorkForCustomerManual(true);
  };

  if (isLoading || !isLoaded) {
    return <Loader />;
  }

  const isSubmitDisabled = !(selectedDate && selectedTime);
  const selectedTimeSlots = selectedDate && timeSlotsByDay[selectedDate] || defaultTimeSlots;
  const displaySantaForm = isLoaded && isEligible && !isBooked;

  return (
    <div className={css.container}>
      {greeting.heading && <h2>{greeting.heading}</h2>}
      {greeting.copy && <p>{greeting.copy}</p>}

      {isLoaded && <Messaging
        slotDetails={slotDetails}
        isLoaded={isLoaded}
        isBooked={isBooked}
        isEligible={isEligible}
        errorCallingApi={errorCallingApi}
        dayBooked={dayBooked}
        timeBooked={timeBooked}
        timezone={timezone}
        showTimeDoesntWorkForCustomer={showTimeDoesntWorkForCustomer}
        showTimeDoesntWorkForCustomerManual={showTimeDoesntWorkForCustomerManual} />}
      {displaySantaForm &&
        <form className={css.form} onSubmit={handleSubmit} method="post">
          <div className={css.calendar}>
            <Calendar
              eligibleDays={eligibleDays}
              timeSlotsByDay={timeSlotsByDay}
              slotDetails={slotDetails}
              onDateSelect={handleDateSelect}
              selectedDate={selectedDate}
            />
          </div>
          <div className={css.time}>
            <TimeSlots
              slotDetails={slotDetails}
              onTimeSelect={handleTimeSelection}
              onChangeTimezone={handleChangeTimezone}
              selectedTimeSlots={selectedTimeSlots}
              timeSelected={selectedTime}
              timezone={timezone}
              isDateSelected={!!selectedDate}
              supportedTimeZones={supportedTimeZones}
            />
            <button className={css.submitButton} disabled={isSubmitDisabled} type="submit">{primary.cta}</button>
            <button className={css.noTime} onClick={handleTimeDoesntWorkForCustomerClick} type="button">{alternative.cta}</button>
          </div>
        </form>}
    </div>
  );
};

const mapStateToProps = ({ meetSanta }) => ({ meetSanta });

export default connect(mapStateToProps, { clearSantaError, fetchSantaAvailableTimeslots, setTimeslotTimezone, reserveSantaTimeslot, onSantaPageLoaded })(SantasHelpers);
